import Component from '../vendor/Component'
import KlevuSearchApiLayer from './KlevuSearchApiLayer'
import KlevuSearchProductCard from './KlevuSearchProductCard'

export default class KlevuSearchOverlay extends Component {
  constructor (elem, theme, options, ctx) {
    super(elem, theme, options, ctx)

    this.apiLayer = new KlevuSearchApiLayer()
    this.query = ''
    this.inputLeft = '0px'
    this.currentAnimatedTextIndex = 0
  }

  initialiseSelectors () {
    this._headerSearchWrapper = document.querySelector('[data-search-overlay-el="headerSearchWrapper"]')
    this._triggers = document.querySelectorAll('[data-search-overlay-el="trigger"]')
    this._input = document.querySelector('[data-search-overlay-el="input"]')
    this._closeButton = document.querySelector('[data-search-overlay-el="close"]')

    this._animatedTextEls = document.querySelectorAll('[data-search-overlay-el="animatedText"]')
    this._mobileNavTrigger = document.querySelector('[data-search-overlay-el="mobileNavTrigger"]')
    this._mobileNav = document.querySelector('[data-search-overlay-el="mobileNav"]')
    this._mobileNavCloseEls = document.querySelectorAll('[data-mobile-nav="close"]')

    this._defaultView = this._elem.querySelector('[data-search-overlay-el="default"]')
    this._resultsView = this._elem.querySelector('[data-search-overlay-el="results"]')
    this._noResults = this._elem.querySelector('[data-search-overlay-el="noResults"]')
    this._searchSuggestionsSection = this._elem.querySelector('[data-search-overlay-el="searchSuggestionsSection"]')
    this._searchSuggestionsList = this._elem.querySelector('[data-search-overlay-el="searchSuggestionsList"]')
    this._seeAllLink = this._elem.querySelector('[data-search-overlay-el="seeAllLink"]')
    this._collectionResultsSection = this._elem.querySelector('[data-search-overlay-el="collectionResultsSection"]')
    this._collectionResultsList = this._elem.querySelector('[data-search-overlay-el="collectionResultsList"]')
    this._resultsList = this._elem.querySelector('[data-search-overlay-el="resultsList"]')
    this._totalCount = this._elem.querySelector('[data-search-overlay-el="totalCount"]')
    this._backdrop = this._elem.querySelector('[data-search-overlay-el="backdrop"]')
  }

  mount () {
    this.initialiseSelectors()
    this.setupListeners()
  }

  setupListeners () {
    this._triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.openSearchOverlay()
      })
    })

    this._closeButton.addEventListener('click', () => {
      this.hideSearchOverlay()
    })

    this._input.addEventListener('input', (event) => {
      this.handleInput(event)
    })

    this._backdrop.addEventListener('click', () => {
      this.hideSearchOverlay()
    })

    this._mobileNavCloseEls.forEach(el => {
      el.addEventListener('click', () => {
        this._mobileNav.classList.remove('open')
        document.body.classList.remove('fixed')
      })
    });



    window.addEventListener('headerDropdown:rendered', (event) => {
      this.mount()
    })

    document.addEventListener('dropdown:open', (event) => {
      this.hideSearchOverlay()
    })
  }

  handleInput (event) {
    const query = event.target.value

    this.setView(query)
    this.toggleAnimatedTextVisibility(query)

    if (query.length === 0) return

    this.renderProducts(query)
    this.renderCollections(query)
    this.renderSuggestions(query)
    this.setViewAllLink(query)
  }

  toggleAnimatedTextVisibility (query) {
    this._animatedTextEls.forEach(el => {
      el.setAttribute('aria-hidden', query.length > 0)
    });
  }

  setViewAllLink (query) {
    const searchRootUrl = this._seeAllLink.dataset.searchRootUrl
    this._seeAllLink.setAttribute('href', `${searchRootUrl}?q=${query}`)
  }

  setTotalCount (response) {
    const totalCount = this._totalCount.getAttribute('data-search-overlay-total-count-text')
    const totalResultsFound = response.meta?.totalResultsFound
    const totalCountText = totalResultsFound && totalResultsFound > 0 ? `(${totalResultsFound})` : ''

    this._totalCount.textContent = `${totalCount} ${totalCountText}`
  }

  openSearchOverlay () {
    this._mobileNavTrigger.classList.remove('active')
    this._mobileNav.classList.remove('open')
    document.body.classList.remove('no-scroll')

    this._elem.setAttribute('aria-hidden', 'false')
    this._headerSearchWrapper.classList.add('active')

    this._input.focus()
  }

  hideSearchOverlay () {
    this._elem.setAttribute('aria-hidden', 'true')
    this._headerSearchWrapper.classList.remove('active')
    this._noResults.setAttribute('aria-hidden', 'true')
    this._resultsView.setAttribute('aria-hidden', 'true')
    this._defaultView.setAttribute('aria-hidden', 'false')
    this.query = ''
    this._input.value = ''
    this._input.blur()

    this._animatedTextEls.forEach(el => {
      el.setAttribute('aria-hidden', 'false')
    });
  }

  setView (query) {
    const isFirstInput = this.query.length === 0 && query.length > 0
    this.query = query

    if (query.length === 0 || isFirstInput) {
      const view = isFirstInput ? 'results' : 'default'

      this._defaultView.setAttribute('aria-hidden', view !== 'default')
      this._resultsView.setAttribute('aria-hidden', view !== 'results')
    }
  }

  async renderProducts (query) {
    const products = await this.apiLayer.fetchProducts(query)
    const productResults = products.queryResults[0]?.records

    if (productResults.length === 0) {
      this._noResults.setAttribute('aria-hidden', 'false')
      this.setView('')
    }

    const fragment = document.createDocumentFragment()

    const listItemPromises = productResults.map(async (productResult) => {
      const productCard = new KlevuSearchProductCard();
      const productHandle = productResult.url.split('/products/')[1].replace(/\/.*$/, '');
      const productCardElement = await productCard.fetchProductCard(productHandle);

      const listItem = document.createElement('li');
      listItem.insertAdjacentHTML('beforeend', productCardElement);

      return listItem;
    });

    const listItems = await Promise.all(listItemPromises);

    listItems.forEach(listItem => {
      fragment.appendChild(listItem);
    });

    this._resultsList.innerHTML = ''
    this._resultsList.appendChild(fragment)

    window.dispatchEvent(new CustomEvent('quickSearch:cardsRendered'));
    this.setTotalCount(products.queryResults[0])
  }

  async renderCollections (query) {
    const collections = await this.apiLayer.fetchCollections(query)
    const collectionsResults = collections.queryResults[0]?.records

    this._collectionResultsList.innerHTML = '';

    if (collectionsResults.length === 0) {
      this._collectionResultsSection.setAttribute('aria-hidden', 'true')
      return
    }

    const fragment = document.createDocumentFragment();

    collectionsResults.forEach(collection => {
      const html = this.collectionListItem(collection);
      const template = document.createElement('template');

      template.innerHTML = html.trim();
      const li = template.content.firstChild;

      fragment.appendChild(li);
    });


    this._collectionResultsList.appendChild(fragment);
    this._collectionResultsSection.setAttribute('aria-hidden', 'false')
  }

  collectionListItem (item) {
    return `<li class="search-overlay__aside-list-item">
      <a href="${item.url}" class="search-overlay__aside-link">
        ${item.name}
      </a>
    </li>`
  }

  async renderSuggestions (query) {
    const suggestions = await this.apiLayer.fetchSuggestions(query)
    const suggestionResults = suggestions.suggestionResults[0]?.suggestions

    this._searchSuggestionsList.innerHTML = '';

    if (suggestionResults.length === 0) {
      this._searchSuggestionsSection.setAttribute('aria-hidden', 'true')
      return
    }

    const fragment = document.createDocumentFragment();

    suggestionResults.forEach(suggestion => {
      const html = this.suggestionListItem(suggestion);
      const template = document.createElement('template');

      template.innerHTML = html.trim();
      const li = template.content.firstChild;

      fragment.appendChild(li);
    });

    this._searchSuggestionsList.appendChild(fragment);
    this._searchSuggestionsSection.setAttribute('aria-hidden', 'false')
  }

  suggestionListItem (item) {
    const suggestionSpan = item.suggest;
    const searchTerm = suggestionSpan.replace(/<[^>]*>/g, '').trim();
    const urlRoot = window.Shopify?.routes?.root || '/';

    return `<li class="search-overlay__aside-list-item">
      <a href="${urlRoot}search?q=${searchTerm}" class="search-overlay__aside-link">
        ${suggestionSpan}
      </a>
    </li>`
  }

  setInputPosition () {
    if (window.innerWidth < 1400) {
      this._headerSearchWrapper.removeAttribute('style');
      return
    }

    const leftLimitEl = document.querySelector('.header__logo-image');

    if (!leftLimitEl || !this._trigger || !this._input) return;

    const leftLimitRect = leftLimitEl.getBoundingClientRect();
    const positionLeft = `${leftLimitRect.right}px`

    const headerContent = document.querySelector('.header__content-wrapper').getBoundingClientRect();
    const headerIcons = document.querySelector('.header__icon-list').getBoundingClientRect();

    this._headerSearchWrapper.style.width = `${headerContent.width - headerIcons.width - 95}px`
    this._headerSearchWrapper.style.left = positionLeft;
    this.inputLeft = positionLeft;
  }

  inputLeftPositionMediaQuery (event) {
    const leftValue = event.matches ? this.inputLeft : '0px';
    this._headerSearchWrapper.style.left = leftValue;
  }

  type () {
    if (this.typerwriterIsStopped) return;

    if (this.typewriterTexts.length) {
      const currentText = this.typewriterTexts[this.typewriterCurrentTextIndex];
      let displayText = currentText.substring(0, this.typewriterCurrentCharIndex);
      this.typewriterElement.textContent = displayText;

      if (!this.typewriterIsDeleting && this.typewriterCurrentCharIndex < currentText.length) {
        this.typewriterCurrentCharIndex++;
        this.typewriterTimeoutId = setTimeout(() => this.type(), this.typewriterSpeed);

      } else if (this.typewriterIsDeleting && this.typewriterCurrentCharIndex > 0) {
        this.typewriterCurrentCharIndex--;
        this.typewriterTimeoutId = setTimeout(() => this.type(), this.typewriterDeleteSpeed);

      } else {
        this.typewriterIsDeleting = !this.typewriterIsDeleting;

        if (!this.typewriterIsDeleting) {
          this.typewriterCurrentTextIndex = (this.typewriterCurrentTextIndex + 1) % this.typewriterTexts.length;
        }

         this.typewriterTimeoutId = setTimeout(() => this.type(), this.typewriterPauseDuration);
      }
    }
  }

  startTypewriter () {
    if (!this.typerwriterIsStopped) return;

    this.typerwriterIsStopped = false;
    this.type();
  }

  stopTypewriter () {
    this.typerwriterIsStopped = true;

    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }

    this.typewriterCurrentTextIndex = 0;
    this.typewriterCurrentCharIndex = 0;
    this.typewriterIsDeleting = false;
    this.typewriterElement.textContent = '';
  }
}
